import {Controller} from "stimulus"

export default class extends Controller {
  static get targets() {
    return ["card", "dot", "title", "description"]
  }

  connect() {
    this._triggerElement = null
    this._rafPending = false
    this._rafId = null
    this._boundHandleDocumentClick = this._handleDocumentClick.bind(this)
    this._boundHandleDocumentKeydown = this._handleDocumentKeydown.bind(this)
    this._boundHandleWindowResize = this._handleWindowResize.bind(this)
    this._boundHandleWindowScroll = this._handleWindowScroll.bind(this)

    document.addEventListener("click", this._boundHandleDocumentClick)
    document.addEventListener("keydown", this._boundHandleDocumentKeydown)
    window.addEventListener("resize", this._boundHandleWindowResize)
    window.addEventListener("scroll", this._boundHandleWindowScroll, true)
  }

  disconnect() {
    document.removeEventListener("click", this._boundHandleDocumentClick)
    document.removeEventListener("keydown", this._boundHandleDocumentKeydown)
    window.removeEventListener("resize", this._boundHandleWindowResize)
    window.removeEventListener("scroll", this._boundHandleWindowScroll, true)
    if (this._rafId) {
      window.cancelAnimationFrame(this._rafId)
      this._rafId = null
      this._rafPending = false
    }
  }

  _handleDocumentClick(event) {
    const trigger = event.target.closest(".fwa-score-category-term")
    if (!trigger) {
      this._hide()
      return
    }

    if (this._isOpen() && this._triggerElement === trigger) {
      this._hide()
      return
    }

    this._showFromTrigger(trigger)
  }

  _handleDocumentKeydown(event) {
    if (event.key === "Escape") {
      this._hide({returnFocus: true})
    }
  }

  _handleWindowResize() {
    this._queueReposition()
  }

  _handleWindowScroll() {
    this._queueReposition()
  }

  _showFromTrigger(trigger) {
    const title = trigger.getAttribute("data-score-category-title") || trigger.textContent.trim()
    const description = trigger.getAttribute("data-score-category-description") || ""
    const dotColor = trigger.getAttribute("data-score-category-dot-color") || "#9CA3AF"

    if (this._triggerElement && this._triggerElement !== trigger) {
      this._triggerElement.removeAttribute("aria-describedby")
    }
    this._triggerElement = trigger
    if (this.element.id) {
      this._triggerElement.setAttribute("aria-describedby", this.element.id)
    }
    this.titleTarget.textContent = `${title}:`
    this.descriptionTarget.textContent = description
    this.dotTarget.style.backgroundColor = dotColor

    this.element.classList.remove("hidden")
    this.element.setAttribute("aria-hidden", "false")

    this._positionTooltip()
  }

  _positionTooltip() {
    if (!this._triggerElement || !this.hasCardTarget) return

    const triggerRect = this._triggerElement.getBoundingClientRect()
    const cardRect = this.cardTarget.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const gap = 10
    const margin = 12
    const cardWidth = cardRect.width || 247
    const cardHeight = cardRect.height || 180

    const triggerIsOffscreen = triggerRect.bottom < 0 || triggerRect.top > viewportHeight || triggerRect.right < 0 || triggerRect.left > viewportWidth
    if (triggerIsOffscreen) {
      this._hide()
      return
    }

    let left = triggerRect.left
    left = Math.max(margin, Math.min(left, viewportWidth - cardWidth - margin))

    let top = triggerRect.bottom + gap
    if (top + cardHeight > viewportHeight - margin) {
      top = triggerRect.top - cardHeight - gap
    }
    top = Math.max(margin, top)

    this.element.style.left = `${Math.round(left)}px`
    this.element.style.top = `${Math.round(top)}px`
  }

  _hide({returnFocus = false} = {}) {
    if (!this._isOpen()) return
    if (this._rafId) {
      window.cancelAnimationFrame(this._rafId)
      this._rafId = null
      this._rafPending = false
    }
    if (this._triggerElement) {
      this._triggerElement.removeAttribute("aria-describedby")
      if (returnFocus && document.contains(this._triggerElement) && typeof this._triggerElement.focus === "function") {
        this._triggerElement.focus()
      }
    }
    this.element.classList.add("hidden")
    this.element.setAttribute("aria-hidden", "true")
    this._triggerElement = null
  }

  _isOpen() {
    return !this.element.classList.contains("hidden")
  }

  _queueReposition() {
    if (!this._isOpen() || this._rafPending) return
    this._rafPending = true
    this._rafId = window.requestAnimationFrame(() => {
      this._rafPending = false
      this._rafId = null
      if (this._isOpen()) this._positionTooltip()
    })
  }
}
